import { SerializedStyles } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ref, memo } from "react";
import { useForm } from "react-hook-form";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles } from "src/apps/AppFlow/pages/styles";
import { FormFlowPage, InfoAlert, TextInput } from "src/components";
import useMergedRef from "src/hooks/useMergedRef";
import { Lock } from "src/icons";
import * as z from "zod";

import { colors, parsers } from "@fraction/shared";

const SCHEMA = z.object({
  ssn: parsers.applicant.SIN,
});

export interface SocialXNumberCAProps {
  onChange: (value: string) => void;
  value?: string;
  title?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const SocialXNumberCA = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  title,
  ...props
}: SocialXNumberCAProps) => {
  const { submitHandler, nextDisabled, nextLoading } = usePartialSubmit({ onNext });

  const { register, handleSubmit, errors } = useForm({
    resolver: zodResolver(SCHEMA),
    reValidateMode: "onSubmit",
  });

  const merged = useMergedRef(register, focusRef);
  const ref = focusRef ? merged : register;

  return (
    <FormFlowPage
      {...props}
      title={title || "What's your Social Insurance Number?"}
      description={
        "This is your 9 digit code that Fraction will use to perform a credit check on you. " +
        "It's not required to provide this but it will speed up the process."
      }
      buttonText={value?.length ? "Next" : "Skip"}
      onNext={handleSubmit(submitHandler)}
      onBack={onBack}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      style={style}
    >
      <TextInput
        error={errors?.ssn?.message}
        ref={ref}
        style={styles.input}
        mask={["999-999-999"]}
        name="ssn"
        label="Social Insurance Number (optional)"
        inputMode="numeric"
        placeholder="123-456-678"
        defaultValue={value}
        onChange={onChange}
        adornment={<Lock height={20} width={20} color={colors.ICON} css={styles.icon} />}
      />
      <InfoAlert style={styles.alert}>
        We use your SIN combined with other personal information to verify your identity and check your
        credit. Initially, we do a <b>hard</b> credit pull, which <b>might affect your credit score.</b>
      </InfoAlert>
    </FormFlowPage>
  );
};

export default memo(SocialXNumberCA);
